/**
 * The D20 account prompt of the venue edit drawer (wave 10) — the pure half.
 *
 * THE RULE IT IMPLEMENTS (owner decision D20, 2026-09-12): typing a «Куратор — почта»
 * no longer creates anything on the api side, and a login attempt creates nothing
 * either. The ACCOUNT is materialised HERE, explicitly, by the person editing the
 * venue: the drawer asks «Создать пользователя для этого куратора?» the moment a save
 * would set a curator address that has no `app_users` row, and
 *
 *   * «да»  → `POST /api/users` (#25.1 semantics: role КУРАТОР = the `VIEWER` enum
 *     value, one curator venue link) and THEN one #8 patch carrying BOTH the link set
 *     and the legacy text column, so the link and the contact text move together;
 *   * «нет» → the #8 patch stores the address only, and the drawer keeps showing a
 *     CLIENT-ONLY reminder («пользователь не создан») with a «создать пользователя»
 *     button — there is no server flag field for it.
 *
 * The existence check is CASE-INSENSITIVE on purpose: the api stores the address
 * lower-cased (#8) but a row may have been created (#25.1) from any spelling, and the
 * e-mail-code login normalizes its request — a case drift must not read as "no user".
 *
 * Two facts a newcomer would misread, stated where the code is:
 *   * a МЕНЕДЖЕР is never created from this drawer — manager accounts are made on the
 *     users page (#25.1 with manager links), because the venue drawer has no manager
 *     creation flow and must not grow one;
 *   * changing the address does NOT remove the previous curator's LINK: the old
 *     `venue_curators` row stays until «Куратор площадок» explicitly drops it (the
 *     drawer hint says so), which is why the text column and the link set can drift —
 *     and why the prompt offers to create, never to re-link.
 *
 * A8 (T.Z12) grew the venue-card half into the same file: the venue's LINKED curator
 * users (`#7`'s `curatorUserIds`, the `venue_curators` rows) resolved against the same
 * directory, for the «Кураторы площадки» block of the card and of the edit drawer —
 * see {@link curatorUsersOf}. The block is the visibility the legacy columns never had:
 * a curator who can actually sign in was invisible on the venue page, while the sheet's
 * «Куратор» text (a contact, no account) looked like the whole story.
 *
 * T.Z23 (owner, wave 11) grew the flow's own decisions in here: a first click on
 * «Создать пользователя» used to look like nothing happened while the account WAS
 * created (the follow-up #8 patch answered 500 on a duplicate link — fixed api-side),
 * the second click then read 409 as an error, and the new name showed up only after a
 * reload. The rules now: one flow at a time (the component's ref guard), a 409 `CONFLICT`
 * on the POST is the goal reached, not a failure ({@link isCuratorEmailTaken}), the
 * settled attempt(s) fold into one outcome ({@link curatorCreateOutcome}), and the
 * directory is re-read so the name renders without a reload.
 *
 * T.Z29 (owner, wave 11) took the last step of that same flow away from the flow:
 * a SUCCESS does not close the pane. The drawer used to call `onClose()` at the end
 * of `createAndSave`, so the pane the operator had just asked a question of vanished
 * before the answer was visible. The settled branch is {@link curatorPaneAfterCreate}
 * now — the pane stays open, the success notice is the model's sentence, and the
 * operator closes the pane themselves.
 *
 * Framework-free AND bundler-free, like every other `*.model.ts` here: the `unit`
 * Playwright project imports it in plain Node.
 */

/** The picker-grade identity the venue drawer already loads for the manager combobox (25.5). */
export interface DirectoryEntryLike {
  id: number;
  email: string;
  displayName: string | null;
}

/** Trimmed + lower-cased, the spelling the api stores and the login normalizes to. */
export function normalizeCuratorEmail(raw: string | null | undefined): string {
  return (raw ?? '').trim().toLowerCase();
}

/**
 * The `app_users` row for the typed address, or `null` when the address has none —
 * the predicate the prompt exists for. Case-insensitive (see the module doc).
 */
export function findCuratorAccount(
  directory: ReadonlyArray<DirectoryEntryLike>,
  rawEmail: string | null | undefined,
): DirectoryEntryLike | null {
  const email = normalizeCuratorEmail(rawEmail);
  if (email === '') return null;
  return directory.find((entry) => entry.email.trim().toLowerCase() === email) ?? null;
}

/** The three states a venue's curator address can be in against the accounts. */
export type CuratorAccountState =
  /** No address on the venue — nothing to create, nothing to remind about. */
  | 'none'
  /** An address WITH an account: the normal, self-serviceable state. */
  | 'linked'
  /** An address WITHOUT an account: the reminder shows until someone creates one. */
  | 'missing';

export function curatorAccountState(input: {
  email: string | null | undefined;
  directory: ReadonlyArray<DirectoryEntryLike>;
  /** The link set the venue already carries (`VenueDetailDto.curatorUserIds`). */
  curatorUserIds?: ReadonlyArray<number>;
}): CuratorAccountState {
  const email = normalizeCuratorEmail(input.email);
  if (email === '') return 'none';
  return findCuratorAccount(input.directory, email) !== null ? 'linked' : 'missing';
}

/**
 * Should saving THIS draft open the prompt? Only when the save would SET a curator
 * address that has no account — i.e. the address is new on this venue (set or changed
 * against the stored one) and no `app_users` row answers it. A venue saved with the
 * same, already-accounted address (or with the address cleared) saves straight through.
 */
export function curatorPromptNeeded(input: {
  storedEmail: string | null | undefined;
  draftEmail: string | null | undefined;
  directory: ReadonlyArray<DirectoryEntryLike>;
}): boolean {
  const draft = normalizeCuratorEmail(input.draftEmail);
  if (draft === '') return false;
  if (draft === normalizeCuratorEmail(input.storedEmail)) return false;
  return findCuratorAccount(input.directory, draft) === null;
}

/** The link set with the new account added, ascending and deduplicated (#8's shape). */
export function curatorUserIdsWith(
  current: ReadonlyArray<number> | null | undefined,
  userId: number,
): number[] {
  return [...new Set([...(current ?? []), userId])].sort((a, b) => a - b);
}

/** #25.1's body for the venue's curator: role КУРАТОР, one curator link, this address. */
export interface CuratorAccountCreateInput {
  email: string;
  displayName: string;
  venueId: number;
  /** Present when the draft cannot be sent: the field the operator must fill. */
  error?: 'empty-email' | 'empty-name';
}

export function curatorAccountCreateFrom(input: {
  email: string | null | undefined;
  displayName: string | null | undefined;
  venueId: number;
}): CuratorAccountCreateInput {
  const email = normalizeCuratorEmail(input.email);
  const displayName = (input.displayName ?? '').trim();
  if (email === '') return { email: '', displayName, venueId: input.venueId, error: 'empty-email' };
  if (displayName === '') return { email, displayName: '', venueId: input.venueId, error: 'empty-name' };
  return { email, displayName, venueId: input.venueId };
}

/** The drawer's copy (RU, T.R1) — kept here so the component stays wiring only. */
export const CURATOR_PROMPT_TITLE = 'Создать пользователя для этого куратора?';
export const CURATOR_PROMPT_HINT =
  'Вход по коду на этот адрес работает, только если для него есть аккаунт. Без него куратор заявку кода получит, но не войдёт.';
export const CURATOR_REMINDER_TITLE = 'Пользователь не создан';
export const CURATOR_REMINDER_HINT =
  'Адрес сохранён на карточке площадки, но аккаунта с ним нет: войти по коду куратор не сможет.';
export const CURATOR_CREATE_BUTTON = 'Создать пользователя';
export const CURATOR_SKIP_BUTTON = 'Сохранить без пользователя';
export const CURATOR_NAME_REQUIRED = 'Укажите «Куратор — ФИО» — оно станет именем аккаунта.';
export const CURATOR_OLD_LINK_HINT =
  'Смена адреса не снимает ссылку прежнего куратора: «Куратор площадок» остаётся, пока её не убрать там явно.';
export const CURATOR_MANAGER_NOTE = 'Менеджеров здесь не создают — их заводят на странице пользователей.';

// ---------------------------------------------------------------------------
// T.Z23 — the create flow's own decisions, the half the double-click bug lived in
// ---------------------------------------------------------------------------

/**
 * The slice of a failed call this file may reason about. Structural on purpose: the
 * model stays framework-free AND bundler-free (the plain-Node `unit` project imports
 * it), so the transport's `ApiError` is read through its documented `status`/`code`
 * fields instead of imported.
 */
export interface CuratorFailureShape {
  status?: number;
  code?: string;
  message?: string;
}

/**
 * Is this failure the api saying "an account with this address already exists"? #25.1
 * answers 409 `CONFLICT` for a normalized duplicate — and for THIS button that answer
 * is not a failure: the account existing is the whole goal (T.Z23). It is what the
 * second POST of a double-click gets back, what a stale tab gets, and what an operator
 * who re-opens the drawer a minute later gets. Everything else (400, 403, a dropped
 * connection) is still a failure the operator must see.
 */
export function isCuratorEmailTaken(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const shape = error as CuratorFailureShape;
  return shape.code === 'CONFLICT' || shape.status === 409;
}

/** One settled `POST /users` attempt of the create flow, as the component observed it. */
export interface CuratorCreateAttempt {
  /** The call came back 2xx. */
  ok: boolean;
  /** The created account's id, when the answer carried one (a 409 carries none). */
  userId?: number;
  /** The rejection, when `ok` is false. */
  error?: unknown;
}

/** What the flow should do after the attempt(s) settled. */
export interface CuratorCreateOutcome {
  /** The goal is reached — an account with this address exists — so the link step may run. */
  ok: boolean;
  /** The account's id to link; `null` = a duplicate was reported but not identified yet. */
  userId: number | null;
  /** At least one attempt answered "address taken" — success the UI must not print as an error. */
  duplicate: boolean;
}

/**
 * Fold the settled `POST /users` attempt(s) into one outcome.
 *
 * The array is the point: a double-click used to fire the write twice, and the SECOND
 * answer (409) arrived after the first (201) and overwrote the success with an error.
 * Folding the attempts means the order and the count stop mattering — one created row
 * or one tolerated duplicate is success, the created row's id wins over the
 * directory-resolved one, and two 409s are still success with the id still to resolve
 * (the component reads a fresh directory for it). A genuine failure with no duplicate
 * beside it stays a failure.
 */
export function curatorCreateOutcome(attempts: ReadonlyArray<CuratorCreateAttempt>): CuratorCreateOutcome {
  let userId: number | null = null;
  let duplicate = false;
  for (const attempt of attempts) {
    if (attempt.ok && typeof attempt.userId === 'number') {
      // The created row's own id is the authority; a later duplicate must not move it.
      userId = attempt.userId;
      continue;
    }
    if (isCuratorEmailTaken(attempt.error)) duplicate = true;
  }
  return { ok: userId !== null || duplicate, userId, duplicate };
}

/** The two sentences the flow can still owe the operator (T.Z23). */
export const CURATOR_TAKEN_UNRESOLVED_TEXT =
  'Пользователь с таким адресом уже есть, но найти его в справочнике не удалось — обновите страницу и проверьте блок «Кураторы площадки».';
export const CURATOR_CREATE_FAILED_TEXT = 'Не удалось создать пользователя.';
export const CURATOR_LINK_FAILED_TEXT = 'Пользователь создан, но привязать его к площадке не удалось — повторите сохранение.';

// ---------------------------------------------------------------------------
// T.Z29 — the SUCCESS half of the flow: the pane stays open
// ---------------------------------------------------------------------------

/** The success notice of the create flow — the owner's «Пользователь создан». */
export const CURATOR_CREATED_TEXT = 'Пользователь создан.';

/** What the pane does once the create-and-link flow has settled. */
export interface CuratorPaneAfterCreate {
  /**
   * T.Z29 (owner, 2026-09-13) — the flow's own writes (#25.1 + the #8 link patch)
   * are NOT a save of the pane, so the flow never closes it: the created curator
   * has to be SEEN in «Кураторы площадки» before anyone decides the pane is done.
   * Closing is the operator's move («Сохранить» / «Отмена» / `Escape`), exactly as
   * it is after a hand-made edit. The unit project pins the whole table — every
   * branch answers `false` here, success included.
   */
  closePane: boolean;
  /** The line the pane prints; `null` = nothing new to say. */
  notice: { tone: 'success' | 'error'; text: string } | null;
}

/**
 * The settled branch of the create flow as ONE decision. The failure sentences
 * stay with the component, where the transport's own message can enrich them;
 * this owns the two cases the model can name in full — a duplicate the directory
 * could not resolve, and the SUCCESS the owner's bug turned into a disappearing
 * drawer.
 */
export function curatorPaneAfterCreate(input: {
  /** The #8 link write landed. */
  linked: boolean;
  /** The account exists (409) but its id stayed unknown, so the link never ran. */
  unresolved: boolean;
}): CuratorPaneAfterCreate {
  if (input.unresolved) {
    return { closePane: false, notice: { tone: 'error', text: CURATOR_TAKEN_UNRESOLVED_TEXT } };
  }
  if (!input.linked) {
    return { closePane: false, notice: { tone: 'error', text: CURATOR_LINK_FAILED_TEXT } };
  }
  return { closePane: false, notice: { tone: 'success', text: CURATOR_CREATED_TEXT } };
}

// ---------------------------------------------------------------------------
// A8 (T.Z12) — the venue's LINKED curator users, the «Кураторы площадки» block
// ---------------------------------------------------------------------------

/** One linked curator user of the venue, printable on the card and in the drawer. */
export interface CuratorUserLabel {
  id: number;
  /** The display name the directory resolves, else the e-mail, else the bare id. */
  name: string;
  /** The address the sign-in code is sent to; `null` when the directory has no row. */
  email: string | null;
}

/**
 * `#7`'s `curatorUserIds` resolved against the 25.5 directory, in the api's own
 * (ascending) order. An id the directory does not answer degrades to «№ N» with no
 * e-mail — the link row is real, the name is just unknown to this client (an older
 * directory, a stale `venue_curators` row); it is never invented.
 */
export function curatorUsersOf(
  curatorUserIds: ReadonlyArray<number> | null | undefined,
  directory: ReadonlyArray<DirectoryEntryLike>,
): CuratorUserLabel[] {
  return (curatorUserIds ?? []).map((id) => {
    const entry = directory.find((candidate) => candidate.id === id);
    return {
      id,
      name: entry?.displayName?.trim() || entry?.email || `№ ${id}`,
      email: entry?.email ?? null,
    };
  });
}

/** The block's title, on the card and in the drawer — the plural of «Куратор площадок». */
export const CURATOR_USERS_TITLE = 'Кураторы площадки';

/** The one-line marker beside the names on the card: these are the accounts with a way in. */
export const CURATOR_USERS_CARD_HINT = 'есть вход на площадку';

/** The drawer's fuller line, next to the block. */
export const CURATOR_USERS_HINT =
  'Аккаунты с входом на эту площадку — код авторизации приходит на их адрес. Ссылки ведут и в «Куратор площадок» у самого пользователя.';

/** An empty block is a fact an operator acts on: nobody can sign in as this venue's curator. */
export const CURATOR_USERS_EMPTY = 'Не назначены';

/** The legacy sheet columns, clarified wherever they are shown or edited (A8, T.Z12). */
export const CURATOR_LEGACY_HINT = 'Из Квоты-листа — не даёт вход в систему';
